import React, { useEffect, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { SOURCE_LABELS } from "../../lib/industries";
import { Modal, Badge } from "./Fields";
import { SUB_STATUS } from "./ClientSubmissions";
import SubmitToClientModal from "./SubmitToClientModal";
import { Mail, MessageCircle, Phone, Copy, FileText, Link2, Send } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const APP_CLS = { new: "bg-slate-100 text-slate-700", shortlisted: "bg-blue-100 text-blue-700", interview: "bg-amber-100 text-amber-700",
  selected: "bg-teal-100 text-teal-700", offered: "bg-purple-100 text-purple-700", joined: "bg-emerald-100 text-emerald-700",
  on_hold: "bg-orange-100 text-orange-700", rejected: "bg-rose-100 text-rose-700" };

export default function CandidateProfileModal({ pid, jobs, onClose, onChanged, onPreview }) {
  const [data, setData] = useState(null);
  const [linkJob, setLinkJob] = useState("");
  const [submitFor, setSubmitFor] = useState(null);

  const load = () => api.get(`/candidates/${pid}`).then(r => setData(r.data)).catch(() => toast.error("Could not load profile"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pid]);

  const link = async () => {
    try { await api.post(`/candidates/${data.profile.id}/applications`, { job_id: linkJob }); toast.success("Linked to job"); setLinkJob(""); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <Modal title="Candidate Profile" onClose={onClose} wide testid="cand-profile-modal"><div className="py-10 text-center text-slate-500">Loading…</div></Modal>;
  const p = data.profile;
  const linked = new Set(data.applications.map(a => a.job_id));

  return (
    <Modal title="Candidate Profile" onClose={onClose} wide testid="cand-profile-modal">
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-heading font-medium text-[#0F2A4A]" data-testid="cp-name">{p.full_name}</h2>
            <div className="text-sm text-slate-500">{p.headline || p.preferred_job || "—"}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(p.sources || [p.source]).filter(Boolean).map(s => <Badge key={s} cls="bg-[#C5A059]/15 text-[#0F2A4A]">{SOURCE_LABELS[s] || s}</Badge>)}
              {p.industry && <Badge cls="bg-[#0F2A4A] text-white">{p.industry}</Badge>}
            </div>
          </div>
          <QuickActions p={p} onPreview={onPreview} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Info k="Email" v={p.email} /><Info k="Phone" v={p.phone} />
          <Info k="Location" v={p.current_location} />
          <Info k="Experience" v={p.total_experience ? `${p.total_experience} yrs` : p.experience_text} />
          <Info k="Notice" v={p.notice_period} />
          <Info k="Expected CTC" v={p.expected_salary ? `₹${(p.expected_salary / 100000).toFixed(1)} LPA` : null} />
          <Info k="Added" v={fmt(p.created_at)} /><Info k="Added by" v={p.added_by} />
        </div>
        {p.skills?.length > 0 && <div className="flex flex-wrap gap-1.5">{p.skills.map(s => <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{s}</span>)}</div>}
        {p.recruiter_notes && <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-xs text-slate-700"><b>Notes:</b> {p.recruiter_notes}</div>}

        <Section title={`Applications (${data.applications.length})`} tid="cp-applications">
          {data.applications.map(a => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 border-b border-slate-100 last:border-0" data-testid={`cp-app-${a.id}`}>
              <div><div className="text-sm font-medium text-[#0F2A4A]">{a.job_title}</div><div className="text-[11px] text-slate-400">{a.company_name} · {fmt(a.created_at)}{a.added_by ? ` · by ${a.added_by}` : ""}</div></div>
              <div className="flex items-center gap-2">
                <Badge cls={APP_CLS[a.status]}>{a.status.replace("_", " ")}</Badge>
                {a.submitted_to_client ? <Badge cls="bg-emerald-50 text-emerald-700 border border-emerald-200">Sent to client</Badge> : (
                  <button onClick={() => setSubmitFor(a)} data-testid={`cp-submit-${a.id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-[#0F2A4A] text-white hover:bg-[#1E3A8A] inline-flex items-center gap-1"><Send className="w-3 h-3" /> Submit to Client</button>
                )}
              </div>
            </div>
          ))}
          {data.applications.length === 0 && <div className="text-xs text-slate-400 py-2">Not linked to any job yet.</div>}
          <div className="flex gap-2 mt-3">
            <select value={linkJob} onChange={e => setLinkJob(e.target.value)} data-testid="cp-link-job-select"
              className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-[#C5A059]">
              <option value="">Link to another job…</option>
              {jobs.filter(j => !linked.has(j.id)).map(j => <option key={j.id} value={j.id}>{j.title} · {j.company_name}</option>)}
            </select>
            <button onClick={link} disabled={!linkJob} data-testid="cp-link-job-btn" className="ae-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"><Link2 className="w-3 h-3" /> Link</button>
          </div>
        </Section>

        <Section title={`Client Submissions (${data.client_submissions.length})`} tid="cp-submissions">
          {data.client_submissions.map(s => (
            <div key={s.id} className="flex flex-wrap justify-between gap-2 py-2 border-b border-slate-100 last:border-0 text-sm">
              <div><b className="text-[#0F2A4A]">{s.client_name}</b> · {s.job_title}<div className="text-[11px] text-slate-400">Submitted {fmt(s.submitted_at)}{s.client_feedback ? ` · “${s.client_feedback}”` : ""}</div></div>
              <Badge cls={SUB_STATUS[s.status]?.cls}>{SUB_STATUS[s.status]?.l}</Badge>
            </div>
          ))}
          {data.client_submissions.length === 0 && <div className="text-xs text-slate-400 py-2">Not submitted to any client yet.</div>}
        </Section>

        {data.cv_submissions.length > 0 && (
          <Section title={`CV Submissions (${data.cv_submissions.length})`} tid="cp-cvs">
            {data.cv_submissions.map(c => <div key={c.id} className="text-xs text-slate-600 py-1">{fmt(c.created_at)} · Preferred: {c.preferred_job || "Any"} · {c.cv_filename}</div>)}
          </Section>
        )}
      </div>
      {submitFor && <SubmitToClientModal application={submitFor} onClose={() => setSubmitFor(null)} onSaved={() => { setSubmitFor(null); load(); onChanged(); }} />}
    </Modal>
  );
}

function QuickActions({ p, onPreview }) {
  const digits = (p.phone || "").replace(/\D/g, "");
  const wa = digits.length === 10 ? `91${digits}` : digits;
  const first = (p.full_name || "").split(" ")[0];
  const btn = "text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 transition-colors";
  return (
    <div className="flex flex-wrap gap-2">
      {p.email && <a href={`mailto:${p.email}?subject=${encodeURIComponent("Opportunity from Achievers Enterprise")}&body=${encodeURIComponent(`Hi ${first},\n\nI'm reaching out from Achievers Enterprise regarding an opportunity that matches your profile.\n\nAre you available for a quick call this week?\n\nBest regards,\nAchievers Enterprise\n+91 90990 42472`)}`} data-testid="cp-email" className={`${btn} bg-[#0F2A4A] text-white hover:bg-[#1E3A8A]`}><Mail className="w-3 h-3" /> Email</a>}
      {wa && <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hi ${first}, this is Achievers Enterprise. We have an opportunity matching your profile. When can we chat?`)}`} target="_blank" rel="noreferrer" data-testid="cp-whatsapp" className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}><MessageCircle className="w-3 h-3" /> WhatsApp</a>}
      {p.phone && <a href={`tel:${p.phone}`} data-testid="cp-call" className={`${btn} bg-[#C5A059] text-white hover:bg-[#D4AF37]`}><Phone className="w-3 h-3" /> Call</a>}
      {p.phone && <button onClick={() => { navigator.clipboard.writeText(p.phone); toast.success("Phone copied"); }} data-testid="cp-copy-phone" className={`${btn} border border-slate-200 hover:bg-slate-50`}><Copy className="w-3 h-3" /> Copy</button>}
      {p.resume_path && <button onClick={() => onPreview({ url: fileUrl(p.resume_path), name: p.full_name })} data-testid="cp-resume" className={`${btn} bg-purple-600 text-white hover:bg-purple-700`}><FileText className="w-3 h-3" /> Resume</button>}
    </div>
  );
}

const Info = ({ k, v }) => (
  <div className="rounded-xl bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{k}</div><div className="text-[#0F2A4A] truncate">{v || "—"}</div></div>
);
const Section = ({ title, children, tid }) => (
  <div data-testid={tid}><h4 className="font-heading font-medium text-[#0F2A4A] mb-2 text-sm">{title}</h4><div className="rounded-xl border border-slate-100 px-4 py-2">{children}</div></div>
);
